import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { CenterPositionedRow } from 'components/flex-containers';
import { BoldLabel, SmallLightLabel } from 'components/labels';
import { GridLayout } from 'components/layouts';
import { getTrans } from 'modules/i18n';
import {
  GENERAL_STATUS_MAP,
  GeneralStatus,
  StatusBadge
} from 'modules/status-badge';
import { getHoursAndMinutesLabel } from 'utils/date';
import { useShift } from '../hooks';
import { getEffectiveValueLabels } from '../utils';
import { SummarySkeleton } from './SummarySkeleton';

export const Summary = () => {
  const theme = useTheme();
  const { shift, isLoading } = useShift();

  if (isLoading) {
    return <SummarySkeleton />;
  }

  if (!shift) return null;

  const {
    shiftStatus,
    reportedShiftWorkMinutes,
    reportedShiftBreakMinutes,
    effectiveShiftWorkMinutes,
    effectiveShiftBreakMinutes
  } = shift;

  const reportedTotalMinutesLabel = getHoursAndMinutesLabel(
    (reportedShiftWorkMinutes ?? 0) + (reportedShiftBreakMinutes ?? 0)
  );
  const reportedWorkedMinutesLabel = getHoursAndMinutesLabel(
    reportedShiftWorkMinutes ?? 0
  );
  const reportedBreakMinutesLabel = getHoursAndMinutesLabel(
    reportedShiftBreakMinutes ?? 0
  );

  const effectiveValueLabels = getEffectiveValueLabels(
    effectiveShiftWorkMinutes,
    reportedShiftWorkMinutes,
    effectiveShiftBreakMinutes,
    reportedShiftBreakMinutes,
    shiftStatus
  );

  const showEffectiveValues = Boolean(
    effectiveValueLabels.workedMinutes ?? effectiveValueLabels.breakMinutes
  );

  return (
    <>
      {shiftStatus && shiftStatus !== GeneralStatus.REPORTED && (
        <CenterPositionedRow sx={{ pb: 1 }}>
          <StatusBadge status={shiftStatus} statusMap={GENERAL_STATUS_MAP} />
        </CenterPositionedRow>
      )}
      <GridLayout
        sx={{ paddingTop: 1, alignItems: 'flex-start' }}
        columns={3}
        elements={[
          <Box key="1">
            <BoldLabel>{getTrans('shiftDetailsPage.reportedTotal')}</BoldLabel>
            <SmallLightLabel crossed={!!effectiveValueLabels.totalMinutes}>
              {reportedTotalMinutesLabel}
            </SmallLightLabel>
            {showEffectiveValues && (
              <SmallLightLabel style={{ color: theme.palette.primary.light }}>
                {effectiveValueLabels.totalMinutes}
              </SmallLightLabel>
            )}
          </Box>,
          <Box key="2" sx={{ textAlign: 'center' }}>
            <BoldLabel>{getTrans('shiftDetailsPage.reportedWorked')}</BoldLabel>
            <SmallLightLabel crossed={!!effectiveValueLabels.workedMinutes}>
              {reportedWorkedMinutesLabel}
            </SmallLightLabel>
            {showEffectiveValues && (
              <SmallLightLabel style={{ color: theme.palette.primary.light }}>
                {effectiveValueLabels.workedMinutes ?? ''}
              </SmallLightLabel>
            )}
          </Box>,
          <Box key="3" sx={{ textAlign: 'end' }}>
            <BoldLabel>{getTrans('shiftDetailsPage.reportedBreak')}</BoldLabel>
            <SmallLightLabel crossed={!!effectiveValueLabels.breakMinutes}>
              {reportedBreakMinutesLabel}
            </SmallLightLabel>
            {showEffectiveValues && (
              <SmallLightLabel style={{ color: theme.palette.primary.light }}>
                {effectiveValueLabels.breakMinutes ?? ''}
              </SmallLightLabel>
            )}
          </Box>
        ]}
      />
    </>
  );
};
